// import { createContext, useContext, useState, useEffect, useRef } from 'react';
// import axios from 'axios';
// import { handleTokenRefresh } from '../utils/refreshToken';

// const AuthContext = createContext();

// // ✅ Always use '/api' and let dev proxy or production server handle it
// export const api = axios.create({
//   baseURL: '/api',
//   timeout: 10000,
//   headers: {
//     'Content-Type': 'application/json'
//   }
// });

// export function AuthProvider({ children }) {
//   const [user, setUser] = useState(null);
//   const [token, setToken] = useState(localStorage.getItem('token'));
//   const [isLoading, setIsLoading] = useState(true);
//   const logoutRef = useRef(() => {});

//   const logout = () => {
//     alert('Session expired. Please log in again.');
//     localStorage.removeItem('token');
//     localStorage.removeItem('refreshToken');
//     setUser(null);
//     setToken(null);
//   };

//   useEffect(() => {
//     logoutRef.current = logout;

//     const reqInterceptor = api.interceptors.request.use(config => {
//       const token = localStorage.getItem('token');
//       if (token) config.headers.Authorization = `Bearer ${token}`;
//       return config;
//     });

//     const resInterceptor = api.interceptors.response.use(
//       response => response,
//       async error => {
//         const originalRequest = error.config;

//         if (error.response?.status === 401 && !originalRequest._retry) {
//           originalRequest._retry = true;
//           try {
//             const newToken = await handleTokenRefresh();
//             localStorage.setItem('token', newToken);
//             setToken(newToken);
//             originalRequest.headers.Authorization = `Bearer ${newToken}`;
//             return api(originalRequest);
//           } catch (error) {
//             logoutRef.current();
//           }
//         }
//         return Promise.reject(error);
//       }
//     );

//     return () => {
//       api.interceptors.request.eject(reqInterceptor);
//       api.interceptors.response.eject(resInterceptor);
//     };
//   }, []);

//   useEffect(() => {
//     if (!token) setIsLoading(false);
//   }, [token]);

//   useEffect(() => {
//     if (!user && token) {
//       const fetchProfile = async () => {
//         try {
//           const { data } = await api.get('/users/profile');
//           setUser(data);
//         } catch (err) {
//           logoutRef.current();
//         } finally {
//           setIsLoading(false);
//         }
//       };
//       fetchProfile();
//     }
//   }, [token]);

//   const login = async (email, password) => {
//     try {
//       const { data } = await api.post('/users/login', { email, password });
//       localStorage.setItem('token', data.accessToken);
//       localStorage.setItem('refreshToken', data.refreshToken);
//       setToken(data.accessToken);

//       const profileRes = await api.get('/users/profile', {
//         headers: { Authorization: `Bearer ${data.accessToken}` }
//       });
//       setUser(profileRes.data);
//       return data;
//     } catch (error) {
//       console.error('❌ Login error:', error);
//       throw error.response?.data || error;
//     }
//   };

//   const signup = async (userData) => {
//     try {
//       const { data } = await api.post('/users/register', userData);
//       localStorage.setItem('token', data.accessToken);
//       localStorage.setItem('refreshToken', data.refreshToken);
//       setToken(data.accessToken);

//       const profileRes = await api.get('/users/profile', {
//         headers: { Authorization: `Bearer ${data.accessToken}` }
//       });
//       setUser(profileRes.data);
//       return data;
//     } catch (error) {
//       throw error.response?.data || error;
//     }
//   };

//   return (
//     <AuthContext.Provider value={{ user, isLoading, token, login, signup, logout }}>
//       {children}
//     </AuthContext.Provider>
//   );
// }

// export const useAuth = () => useContext(AuthContext);

// AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Add axios response interceptor for handling 401 errors
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      response => response,
      async error => {
        if (error.response?.status === 401) {
          await logout();
        }
        return Promise.reject(error);
      }
    );

    return () => api.interceptors.response.eject(interceptor);
  }, []);

  const fetchProfile = async () => {
    try {
      const { data } = await api.get('/users/profile');
      setUser(data);
    } catch (err) {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (userData) => {
    try {
      const response = await api.post('/users/register', userData, {
        withCredentials: true
      });

      // Automatically log in after successful signup
      await login({
        email: userData.email,
        password: userData.password
      });

      // On successful signup and login, fetch profile
      await fetchProfile();

      return { success: true };
    } catch (err) {
      // Handle validation errors with multiple fields
      if (err.response?.data?.errors) {
        const fieldErrors = {};
        err.response.data.errors.forEach(error => {
          fieldErrors[error.field] = error.message;
        });
        throw {
          message: 'Validation failed',
          fieldErrors
        };
      }

      // Handle single field errors
      if (err.response?.data?.field) {
        throw {
          message: err.response.data.message,
          field: err.response.data.field
        };
      }

      throw new Error(err.response?.data?.message || 'Signup failed');
    }
  };

  const login = async (credentials) => {
    try {
      await api.post('/users/login', credentials, {
        withCredentials: true
      });
      await fetchProfile();
    } catch (err) {
      setUser(null);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await api.post('/users/logout', {}, {
        withCredentials: true
      });
    } finally {
      setUser(null);
    }
  };

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        await api.get('/users/verify', {
          withCredentials: true
        });
        await fetchProfile();
      } catch (err) {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    verifyAuth();
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      login,
      logout,
      signup,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
